import { useMemo } from "react";
import { useRolePermissionContext, useRolePermissionsLoading } from "@Front/Stores/RolePermissionsStore";
import { useRolePermissionsMatrixSnapshot } from "@Front/Hooks/useRolePermissionsMatrixSnapshot";
import { filterMenuItemsByCan, type MenuRulePermissionCanFn } from "@Front/Utils/filterMenuItemsByCan";
import type { IItem } from "./MenuItem";
import { useMenuIsSuperAdmin } from "./useMenuIsSuperAdmin";
import { useAppRulePermissionCan } from "@Front/Hooks/useAppRulePermissionCan";

/**
 * Point d’entrée unique pour le filtrage matrice (`can`) des entrées de menu avec `rules` (mode NECESSARY).
 * Super-admin : pas de filtre. Chargement sans `canOverride` : pas de filtre (comportement historique).
 * Menu « Espace office » (`canOverride` défini) : si matrice et contexte sont déjà présents, le filtre matrice
 * s’applique même pendant `loading` (refresh), pour éviter d’afficher toutes les entrées sans contrôle.
 *
 * **Appels** : `useMenuController` (tout `Menu`) ; `OfficeMenuSection` (burger). Seuls « Espace office » passent
 * `useOfficeMenuMatrixCan` — les autres menus (`Menu` dossier, super-admin, design system) gardent `canOverride`
 * absent → `can` du store = contexte JWT / session courante.
 *
 * @param canOverride — ex. `useOfficeMenuMatrixCan` pour « Espace office » (office sélecteur vs contexte invité).
 */
export function useMatrixFilteredMenuItems(
	items: IItem[],
	canOverride?: MenuRulePermissionCanFn,
): { effectiveItems: IItem[]; isSuperAdmin: boolean; loadingPermissions: boolean } {
	const permissionContext = useRolePermissionContext();
	const loadingPermissions = useRolePermissionsLoading();
	const matrix = useRolePermissionsMatrixSnapshot();
	const appRuleCan = useAppRulePermissionCan();
	const can = useMemo<MenuRulePermissionCanFn>(() => {
		if (canOverride) {
			return canOverride;
		}
		return appRuleCan;
	}, [canOverride, appRuleCan]);
	const isSuperAdmin = useMenuIsSuperAdmin(permissionContext, loadingPermissions);
	const hasOfficeMatrixOverride = Boolean(canOverride);
	const applyMatrixWhilePermissionsRefreshing =
		hasOfficeMatrixOverride && matrix != null && permissionContext != null && loadingPermissions;

	const effectiveItems = useMemo(() => {
		if (isSuperAdmin) {
			return items;
		}
		if (loadingPermissions && !applyMatrixWhilePermissionsRefreshing) {
			return items;
		}
		return filterMenuItemsByCan(items, can);
	}, [items, can, isSuperAdmin, loadingPermissions, applyMatrixWhilePermissionsRefreshing]);

	return { effectiveItems, isSuperAdmin, loadingPermissions };
}
